/**
 * Chart Color Utility
 *
 * Provides consistent, deterministic color generation for chart components.
 * Uses HSL-based dynamic color generation to support unlimited distinct colors.
 */

// Fallback expanded color palette (20 colors) for edge cases
const FALLBACK_COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
  "#e41a1c",
  "#377eb8",
  "#4daf4a",
  "#984ea3",
  "#ff7f00",
  "#ffff33",
  "#a65628",
  "#f781bf",
  "#999999",
  "#66c2a5",
];

/**
 * Hash function to generate a consistent numeric value from a string or number ID.
 * Same ID will always produce the same hash value.
 *
 * @param id - The identifier to hash
 * @returns A positive integer hash value
 */
export const hashId = (id: string | number): number => {
  const str = String(id);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

/**
 * Generate an HSL color string based on an ID.
 * Uses HSL color space to maximize color distinction by varying hue.
 *
 * @param id - The identifier to generate a color for
 * @param saturation - Saturation percentage (0-100), default 75%
 * @param lightness - Lightness percentage (0-100), default 55%
 * @returns HSL color string in format "hsl(hue, saturation%, lightness%)"
 */
export const generateHSLColor = (
  id: string | number,
  saturation: number = 75,
  lightness: number = 55,
): string => {
  const hash = hashId(id);

  // Distribute hue evenly across 360 degrees for maximum distinction
  // Use golden ratio conjugate (0.618...) for better distribution
  const goldenRatioConjugate = 0.618033988749895;
  const hue = (hash * goldenRatioConjugate * 360) % 360;

  return `hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`;
};

/**
 * Get a color for a given ID with optional backend color override.
 *
 * @param id - The identifier to generate a color for
 * @param backendColor - Optional color from backend data model
 * @param useFallbackPalette - If true, uses the fallback palette instead of HSL generation
 * @returns Color string (hex or hsl format)
 */
export const getColorForId = (
  id: string | number,
  backendColor?: string,
  useFallbackPalette: boolean = false,
): string => {
  // Use backend color if provided
  if (backendColor) {
    return backendColor;
  }

  // Use fallback palette if requested (for compatibility with existing behavior)
  if (useFallbackPalette) {
    const hash = hashId(id);
    return FALLBACK_COLORS[hash % FALLBACK_COLORS.length];
  }

  // Generate dynamic HSL color
  return generateHSLColor(id);
};

/**
 * Generate a color palette for an array of items.
 * Ensures consistent colors for the same IDs across renders.
 *
 * @param items - Array of items with id property
 * @param colorField - Optional field name for backend color in items
 * @param useFallbackPalette - If true, uses the fallback palette instead of HSL generation
 * @returns Array of color strings matching the items array
 */
export const getColorPalette = <T extends { id?: string | number }>(
  items: T[],
  colorField?: keyof T,
  useFallbackPalette: boolean = false,
): string[] => {
  return items.map((item, index) => {
    const itemId = item.id ?? index;
    const backendColor = colorField
      ? (item[colorField] as unknown as string)
      : undefined;
    return getColorForId(itemId, backendColor, useFallbackPalette);
  });
};

/**
 * Get color by index from fallback palette (for backward compatibility).
 *
 * @param index - Index in the color palette
 * @returns Hex color string
 */
export const getColorByIndex = (index: number): string => {
  return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
};

/**
 * Legacy function for compatibility with existing code.
 * Maps to getColorForId with fallback palette enabled.
 *
 * @deprecated Use getColorForId instead
 */
export const getColorIndexFromId = (id: string | number): number => {
  return hashId(id) % FALLBACK_COLORS.length;
};
